import { defineStore } from 'pinia'
import httpClient from '@/api/httpClient'
import { toString } from 'lodash'
import { useMessagesStore } from '@/stores/messages'
import { camelKeyed } from '@/utils/params_helpers.js'
import { exifMediumContent } from '@/utils/exif_helpers.js'
import { useMediaStore } from '@/stores/media.js'

const buildFormData = ({ file, squeueId, sectionId, credit, cutline }) => {
  let formData = new FormData()

  if (file) formData.append('medium[upload]', file)
  formData.append('medium[squeue_id]', toString(squeueId))
  formData.append('medium[section_id]', toString(sectionId))
  formData.append('medium[credit]', toString(credit))
  formData.append('medium[cutline]', toString(cutline))

  return formData
}

export const useMediumStore = defineStore('medium', {
  state: () => ({
    id: null,
    url: null,
    smallThumbUrl: null,
    largeThumbUrl: null,
    contentType: null,
    file: null,
    filename: null,
    imageBase64: null,
    relativePath: null,
    squeueId: null,
    squeueTitle: null,
    sectionId: null,
    sectionTitle: null,
    cutline: '',
    credit: '',
    createdAt: null,
    stories: []
  }),

  getters: {
    isImage({ contentType }) {
      if (!contentType) return false

      return contentType.split('/')[0] === 'image'
    },

    bestUrl({ url, imageBase64 }) {
      if (!this.isImage) return null

      return imageBase64 || url
    }
  },

  actions: {
    replace(newState) {
      Object.assign(this, newState)
    },

    processFile({ file }) {
      this.file = file
      this.filename = file.name
      this.contentType = file.type
      if (this.isImage) this.imageBase64 = URL.createObjectURL(file)

      return exifMediumContent(file).then(({ cutline, credit }) => {
        if (cutline) this.cutline = cutline
        if (credit) this.credit = credit
      })
    },

    pullSoleFromMedia() {
      const medium = useMediaStore().soleSelected

      if (!medium) return

      this.replace(camelKeyed(medium))
    },

    async create({ file, squeueId, sectionId, credit, cutline }) {
      const formData = buildFormData({ file, squeueId, sectionId, credit, cutline })

      return httpClient.post('/api/internal/media', formData)
        .then(() => {
          useMediaStore().search({ page: 1 })
          this.$reset()
        })
        .catch(err => {
          console.error(err)
          useMessagesStore().addError(err)
        })
    },

    async update({ id, file, squeueId, sectionId, credit, cutline }) {
      const formData = buildFormData({ file, squeueId, sectionId, credit, cutline })

      return httpClient.patch(`/api/internal/media/${id}`, formData)
        .then(() => {
          useMediaStore().search().then(() => {
            this.pullSoleFromMedia()
          })
        })
        .catch(err => {
          console.error(err)
          useMessagesStore().addError(err)
        })
    },

    async destroy({ id }) {
      return httpClient.delete(`/api/internal/media/${id}`)
        .then(() => {
          const mediaStore = useMediaStore()
          mediaStore.clearSelected()
          mediaStore.search()
        })
        .catch(err => {
          console.error(err)
          useMessagesStore().addError(err)
        })
    }
  }
})
